import React, { useEffect, useMemo, useRef, useState } from "react";
import {
  searchNovaPostPublicDivisions,
  searchNovaPostSettlements,
} from "../api/novaPoshtaDirectoryApi";
import styles from "./NovaPoshtaAutoComplete.module.css";

const DIRECTORY_MODES = new Set([
  "settlement",
  "branch",
  "parcelLocker",
  "division",
  "divisionOrLocker",
]);
const FREE_TEXT_MODES = new Set(["street", "courierAddress"]);

const getMessage = (error) =>
  error?.response?.data?.message ||
  error?.message ||
  "Не вдалося завантажити довідник Nova Post.";

const normalizeSettlement = (item = {}) => ({
  provider: "nova-poshta",
  apiVersion: "v.1.0",
  type: "settlement",
  displayName: item.fullName || item.name || "",
  fullAddress: "",
  countryCode: "UA",
  settlement: {
    id: Number(item.id) || null,
    ref: String(item.ref || ""),
    name: String(item.name || ""),
    regionName: String(item.regionName || ""),
    areaName: String(item.areaName || ""),
  },
  division: null,
  address: null,
});

const normalizeDivision = (item = {}) => {
  const settlement = item.settlement || {};
  const division = item.division || item;
  const locker =
    item.type === "locker" || division.category === "Postomat";
  return {
    provider: "nova-poshta",
    apiVersion: "v.1.0",
    type: locker ? "parcelLocker" : "branch",
    displayName: division.name || item.displayName || "",
    fullAddress: division.address || item.fullAddress || "",
    countryCode: item.countryCode || "UA",
    settlement: {
      id: Number(settlement.id) || null,
      ref: String(settlement.ref || ""),
      name: String(settlement.name || ""),
      regionName: String(settlement.regionName || ""),
      areaName: String(settlement.areaName || ""),
    },
    division: {
      id: Number(division.id || division.divisionId) || null,
      divisionId: Number(division.divisionId || division.id) || null,
      ref: String(division.ref || item.warehouseRef || ""),
      number: String(division.number || division.divisionNumber || ""),
      divisionNumber: String(division.divisionNumber || division.number || ""),
      name: String(division.name || ""),
      category: String(division.category || item.divisionCategory || ""),
      address: String(division.address || item.fullAddress || ""),
      status: String(division.status || item.status || ""),
      prohibitedIssuance: Boolean(
        division.prohibitedIssuance || item.prohibitedIssuance
      ),
      settlementId:
        Number(division.settlementId || settlement.id) || null,
    },
    address: null,
  };
};

const normalizeFreeText = (value, settlement = null) => ({
  provider: "nova-poshta",
  apiVersion: "v.1.0",
  type: "courierAddress",
  displayName: value,
  fullAddress: value,
  countryCode: "UA",
  settlement: settlement
    ? {
        id: Number(settlement.id) || null,
        ref: String(settlement.ref || ""),
        name: String(settlement.name || ""),
        regionName: String(settlement.regionName || ""),
        areaName: String(settlement.areaName || ""),
      }
    : null,
  division: null,
  address: {
    streetId: null,
    streetRef: null,
    streetName: value,
    building: "",
    apartment: "",
    entrance: "",
    floor: "",
    fullAddress: value,
  },
});

const NovaPoshtaAutoComplete = ({
  mode = "settlement",
  value,
  initialValue = "",
  initialSelection = null,
  onChange,
  onSelect,
  settlement = null,
  disabled = false,
  label = "",
  className = "",
  placeholder = "",
  validationError = false,
  debounceMs = 350,
  inputProps = {},
}) => {
  const [internalValue, setInternalValue] = useState(initialValue);
  const [items, setItems] = useState([]);
  const [open, setOpen] = useState(false);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");
  const [activeIndex, setActiveIndex] = useState(-1);
  const [selectionLocked, setSelectionLocked] = useState(Boolean(initialSelection));
  const blurTimerRef = useRef(null);
  const controlled = value !== undefined;
  const displayValue = controlled ? String(value || "") : internalValue;
  const directoryMode = DIRECTORY_MODES.has(mode);
  const freeTextMode = FREE_TEXT_MODES.has(mode);
  const divisionMode = directoryMode && mode !== "settlement";
  const dependencyMissing = divisionMode && !settlement?.id && !settlement?.name;
  const isDisabled = disabled || dependencyMissing;
  const minimumLength = mode === "settlement" ? 2 : 1;

  const effectivePlaceholder = useMemo(() => {
    if (dependencyMissing) return "Спочатку оберіть населений пункт";
    if (placeholder) return placeholder;
    if (mode === "settlement") return "Введіть населений пункт";
    if (mode === "parcelLocker") return "Номер або адреса поштомата";
    if (divisionMode) return "Номер або адреса відділення";
    return "Введіть назву вулиці";
  }, [dependencyMissing, divisionMode, mode, placeholder]);

  useEffect(() => {
    if (!directoryMode || isDisabled || selectionLocked) {
      setItems([]);
      return undefined;
    }
    const query = displayValue.trim();
    if (query.length < minimumLength) {
      setItems([]);
      setError("");
      return undefined;
    }

    const controller = new AbortController();
    const requestDelay = /^\d+$/u.test(query) ? 80 : debounceMs;
    const timer = setTimeout(async () => {
      setLoading(true);
      setError("");
      try {
        const result =
          mode === "settlement"
            ? await searchNovaPostSettlements(query, {
                signal: controller.signal,
              })
            : await searchNovaPostPublicDivisions(
                {
                  settlementId: settlement?.id,
                  settlementName: settlement?.name,
                  query,
                  pointType:
                    mode === "parcelLocker"
                      ? "locker"
                      : mode === "divisionOrLocker"
                      ? ""
                      : "branch",
                },
                { signal: controller.signal }
              );
        const normalized = result.map((item) =>
          mode === "settlement"
            ? normalizeSettlement(item)
            : normalizeDivision(item)
        );
        setItems(normalized);
        setActiveIndex(normalized.length ? 0 : -1);
        setOpen(true);
      } catch (requestError) {
        if (
          requestError?.code !== "ERR_CANCELED" &&
          requestError?.name !== "CanceledError"
        ) {
          setItems([]);
          setError(getMessage(requestError));
          setOpen(true);
        }
      } finally {
        if (!controller.signal.aborted) setLoading(false);
      }
    }, requestDelay);

    return () => {
      clearTimeout(timer);
      controller.abort();
    };
  }, [
    debounceMs,
    directoryMode,
    displayValue,
    isDisabled,
    minimumLength,
    mode,
    selectionLocked,
    settlement?.id,
    settlement?.name,
  ]);

  useEffect(
    () => () => {
      if (blurTimerRef.current) clearTimeout(blurTimerRef.current);
    },
    []
  );

  const emitChange = (nextValue) => {
    if (!controlled) setInternalValue(nextValue);
    onChange?.(nextValue);
  };

  const selectItem = (item) => {
    const nextValue =
      mode === "settlement"
        ? item.displayName
        : [item.settlement?.name, item.displayName]
            .filter(Boolean)
            .join(", ");
    emitChange(nextValue);
    onSelect?.(item);
    setSelectionLocked(true);
    setItems([]);
    setOpen(false);
    setError("");
  };

  const handleKeyDown = (event) => {
    if (event.key === "ArrowDown" && items.length) {
      event.preventDefault();
      setOpen(true);
      setActiveIndex((index) => (index + 1) % items.length);
      return;
    }
    if (event.key === "ArrowUp" && items.length) {
      event.preventDefault();
      setOpen(true);
      setActiveIndex((index) => (index <= 0 ? items.length - 1 : index - 1));
      return;
    }
    if (event.key === "Enter") {
      if (open && activeIndex >= 0 && items[activeIndex]) {
        event.preventDefault();
        selectItem(items[activeIndex]);
      } else if (freeTextMode && displayValue.trim()) {
        onSelect?.(normalizeFreeText(displayValue.trim(), settlement));
      }
      return;
    }
    if (event.key === "Escape") setOpen(false);
  };

  return (
    <div
      className={`${styles.field} ${className} ${
        open ? styles.openField : ""
      }`.trim()}
    >
      {label && <label className={validationError ? styles.errorLabel : ""}>{label}</label>}
      <div className={styles.inputWrap}>
        <input
          {...inputProps}
          type="text"
          value={displayValue}
          disabled={isDisabled}
          autoComplete={
            inputProps.autoComplete ||
            (freeTextMode ? "address-line1" : "off")
          }
          role={directoryMode ? "combobox" : undefined}
          aria-autocomplete={directoryMode ? "list" : "none"}
          aria-expanded={open}
          aria-invalid={Boolean(validationError)}
          placeholder={effectivePlaceholder}
          className={[inputProps.className, validationError ? styles.errorInput : ""]
            .filter(Boolean)
            .join(" ")}
          onChange={(event) => {
            const nextValue = event.target.value;
            emitChange(nextValue);
            setSelectionLocked(false);
            onSelect?.(
              freeTextMode && nextValue.trim()
                ? normalizeFreeText(nextValue.trim(), settlement)
                : null
            );
            if (directoryMode) setOpen(true);
            setError("");
          }}
          onFocus={() => {
            if (items.length || error) setOpen(true);
          }}
          onBlur={() => {
            blurTimerRef.current = setTimeout(() => setOpen(false), 150);
          }}
          onKeyDown={handleKeyDown}
        />
        {loading && <span className={styles.spinner} aria-label="Завантаження" />}
        {!isDisabled && displayValue && (
          <button
            type="button"
            className={styles.clearButton}
            aria-label="Очистити поле"
            onMouseDown={(event) => event.preventDefault()}
            onClick={() => {
              emitChange("");
              onSelect?.(null);
              setSelectionLocked(false);
              setItems([]);
              setOpen(false);
            }}
          >
            ×
          </button>
        )}
      </div>
      {open && items.length > 0 && (
        <ul className={styles.listbox} role="listbox">
          {items.map((item, index) => (
            <li
              key={
                item.type === "settlement"
                  ? `settlement-${item.settlement.id}`
                  : `division-${item.division.divisionId}`
              }
              role="option"
              aria-selected={index === activeIndex}
              className={index === activeIndex ? styles.activeOption : ""}
              onMouseDown={(event) => {
                event.preventDefault();
                selectItem(item);
              }}
            >
              <strong>{item.displayName}</strong>
              {item.fullAddress && <span>{item.fullAddress}</span>}
            </li>
          ))}
        </ul>
      )}
      {error && <small className={styles.errorMessage}>{error}</small>}
      {freeTextMode && (
        <small className={styles.helpMessage}>
          У v.1.0 немає окремого довідника вулиць — адресу буде збережено структуровано.
        </small>
      )}
    </div>
  );
};

export default NovaPoshtaAutoComplete;
